/**
 * GameClient — the imperative composition root that binds PixiJS rendering, the
 * camera, fog of war and the deterministic engine simulation into one running
 * game. React mounts it once against a container element and otherwise stays out
 * of the hot loop; telemetry is pushed to the Zustand store a few times a second.
 *
 * Sprint 2 adds: road & unit rendering, the worker movement/AI systems, render
 * interpolation between sim ticks, and pointer tools (draw roads / spawn workers).
 */
import { Application, Container } from 'pixi.js';
import {
  MapGenerator,
  Simulation,
  FixedTimestep,
  MovementSystem,
  WorkerBrainSystem,
  spawnWorker,
  findPath,
  CWorker,
} from '@settlers/engine';
import type { GameMap } from '@settlers/engine';
import {
  worldToGrid,
  gridToWorld,
  TILE_HALF_WIDTH,
  TILE_HALF_HEIGHT,
  TICKS_PER_SECOND,
  Prng,
  asSeed,
} from '@settlers/shared';
import type { Vec2 } from '@settlers/shared';
import { Camera } from './camera/Camera.js';
import { CameraController } from './camera/CameraController.js';
import { IsoRenderer } from './render/IsoRenderer.js';
import { FogOfWar } from './fog/FogOfWar.js';
import { RoadRenderer } from './render/RoadRenderer.js';
import { UnitRenderer } from './render/UnitRenderer.js';
import { PlaceholderAssets } from './assets/AssetRegistry.js';
import { useGameStore } from '../state/store.js';
import type { HoveredTile } from '../state/store.js';

export interface GameClientOptions {
  readonly seed: number;
  readonly mapWidth: number;
  readonly mapHeight: number;
}

const TELEMETRY_INTERVAL_MS = 200;
const INITIAL_WORKERS = 24;

export class GameClient {
  private app: Application | null = null;
  private map!: GameMap;
  private renderer!: IsoRenderer;
  private fog!: FogOfWar;
  private roadRenderer!: RoadRenderer;
  private unitRenderer!: UnitRenderer;
  private camera!: Camera;
  private controller!: CameraController;
  private sim!: Simulation;
  private readonly timestep = new FixedTimestep();
  private readonly worldContainer = new Container();

  private alpha = 0;
  private painting = false;
  private tpsCounter = 0;
  private tpsWindowMs = 0;
  private tpsDisplay = 0;
  private telemetryAcc = 0;

  constructor(private readonly options: GameClientOptions) {}

  async init(container: HTMLElement): Promise<void> {
    const app = new Application();
    await app.init({
      resizeTo: container,
      antialias: true,
      background: 0x0d1017,
      preference: 'webgl',
      autoDensity: true,
      resolution: Math.min(window.devicePixelRatio ?? 1, 2),
    });
    container.appendChild(app.canvas);
    this.app = app;

    // --- World generation + simulation ---
    this.map = new MapGenerator().generate({
      width: this.options.mapWidth,
      height: this.options.mapHeight,
      seed: this.options.seed,
    });
    this.sim = new Simulation({ map: this.map, seed: this.options.seed });
    this.sim.addSystem(new MovementSystem()).addSystem(new WorkerBrainSystem());

    // --- Rendering stack (terrain → roads → units → fog) ---
    const assets = new PlaceholderAssets();
    this.renderer = new IsoRenderer(app.renderer, this.map, assets);
    this.roadRenderer = new RoadRenderer(this.map, this.sim.roads);
    this.unitRenderer = new UnitRenderer(app.renderer, this.sim.world, this.map);
    this.fog = new FogOfWar(app.renderer, this.map);
    this.worldContainer.addChild(this.renderer.stage);
    this.worldContainer.addChild(this.roadRenderer.stage);
    this.worldContainer.addChild(this.unitRenderer.stage);
    this.worldContainer.addChild(this.fog.stage);
    app.stage.addChild(this.worldContainer);

    // --- Camera ---
    this.camera = new Camera();
    this.camera.setViewport(app.renderer.width, app.renderer.height);
    const extent = this.renderer.worldExtent();
    this.camera.setBounds({
      minX: extent.minX,
      maxX: extent.maxX,
      minY: extent.minY,
      maxY: extent.maxY,
    });
    this.camera.centerOn((extent.minX + extent.maxX) / 2, (extent.minY + extent.maxY) / 2);
    this.controller = new CameraController(this.camera, app.canvas);

    this.spawnInitialWorkers();
    this.attachToolListeners(app.canvas);

    useGameStore.getState().setSeed(this.options.seed);
    useGameStore.getState().setReady(true);

    app.ticker.add(() => this.frame(app.ticker.deltaMS));
  }

  private frame(dtMs: number): void {
    const clamped = Math.min(dtMs, 100);

    // Build/spawn tools take over drag; only pan the camera in 'camera' mode.
    const mode = useGameStore.getState().mode;
    this.controller.setDragPanEnabled(mode === 'camera');

    this.controller.update(clamped);
    this.camera.update(clamped);
    this.applyCamera();

    if (!useGameStore.getState().paused) {
      this.alpha = this.timestep.advance(clamped, () => {
        this.sim.step();
        this.tpsCounter++;
      });
    }

    this.revealAroundCamera();
    this.fog.bakeDirty();
    this.roadRenderer.redrawIfNeeded();
    this.unitRenderer.sync(useGameStore.getState().paused ? 1 : this.alpha);
    this.renderer.cull(this.camera);

    this.updateHovered();
    this.pushTelemetry(clamped);
  }

  private applyCamera(): void {
    const origin = this.camera.worldToScreen(0, 0);
    this.worldContainer.scale.set(this.camera.zoom);
    this.worldContainer.position.set(origin.x, origin.y);
    if (this.app) this.camera.setViewport(this.app.renderer.width, this.app.renderer.height);
  }

  private revealAroundCamera(): void {
    const view = this.camera.visibleWorldRect();
    const radiusTiles =
      Math.max(view.w / 2 / TILE_HALF_WIDTH, view.h / 2 / TILE_HALF_HEIGHT) + 2;
    this.fog.reveal(this.camera.focusX, this.camera.focusY, radiusTiles);
  }

  // --- Pointer tools -------------------------------------------------------

  private attachToolListeners(canvas: HTMLCanvasElement): void {
    canvas.addEventListener('pointerdown', this.onToolDown);
    canvas.addEventListener('pointermove', this.onToolMove);
    window.addEventListener('pointerup', this.onToolUp);
  }

  private eventTile(e: PointerEvent): { x: number; y: number } {
    const rect = (this.app as Application).canvas.getBoundingClientRect();
    const world = this.camera.screenToWorld(e.clientX - rect.left, e.clientY - rect.top);
    const g = worldToGrid(world.x, world.y);
    return { x: Math.floor(g.x + 0.5), y: Math.floor(g.y + 0.5) };
  }

  private onToolDown = (e: PointerEvent): void => {
    const mode = useGameStore.getState().mode;
    if (mode === 'road') {
      this.painting = true;
      this.paintRoad(this.eventTile(e));
    } else if (mode === 'spawn') {
      this.spawnWorkerNear(this.eventTile(e));
    }
  };

  private onToolMove = (e: PointerEvent): void => {
    if (this.painting && useGameStore.getState().mode === 'road') {
      this.paintRoad(this.eventTile(e));
    }
  };

  private onToolUp = (): void => {
    this.painting = false;
  };

  private paintRoad(tile: { x: number; y: number }): void {
    if (this.sim.roads.place(tile.x, tile.y)) this.roadRenderer.markDirty();
  }

  // --- Worker spawning -----------------------------------------------------

  private spawnInitialWorkers(): void {
    const rng = new Prng(asSeed(this.options.seed ^ 0x5f3759df));
    let spawned = 0;
    let attempts = 0;
    while (spawned < INITIAL_WORKERS && attempts < INITIAL_WORKERS * 40) {
      attempts++;
      const home = this.randomPassableTile(rng);
      if (!home) continue;
      const work = this.reachableTileNear(home, rng, 8, 22);
      if (!work) continue;
      spawnWorker(this.sim.world, this.map.width, this.map.index(home.x, home.y), this.map.index(work.x, work.y));
      spawned++;
    }
  }

  private spawnWorkerNear(tile: { x: number; y: number }): void {
    if (!this.isPassable(tile.x, tile.y)) return;
    const rng = new Prng(asSeed((this.sim.tick + tile.x * 31 + tile.y * 17) >>> 0));
    const work = this.reachableTileNear(tile, rng, 6, 20) ?? tile;
    spawnWorker(this.sim.world, this.map.width, this.map.index(tile.x, tile.y), this.map.index(work.x, work.y));
  }

  private isPassable(x: number, y: number): boolean {
    return this.map.inBounds(x, y) && Number.isFinite(this.map.moveCostAt(x, y));
  }

  private randomPassableTile(rng: Prng): { x: number; y: number } | null {
    for (let i = 0; i < 30; i++) {
      const x = rng.int(0, this.map.width - 1);
      const y = rng.int(0, this.map.height - 1);
      if (this.isPassable(x, y) && this.map.isBuildable(x, y)) return { x, y };
    }
    return null;
  }

  private reachableTileNear(
    from: { x: number; y: number },
    rng: Prng,
    min: number,
    max: number,
  ): { x: number; y: number } | null {
    for (let i = 0; i < 24; i++) {
      const ang = rng.range(0, Math.PI * 2);
      const dist = rng.range(min, max);
      const x = Math.round(from.x + Math.cos(ang) * dist);
      const y = Math.round(from.y + Math.sin(ang) * dist);
      if (!this.isPassable(x, y)) continue;
      if (findPath(this.sim.nav, from.x, from.y, x, y)) return { x, y };
    }
    return null;
  }

  // --- Hover + telemetry ---------------------------------------------------

  private updateHovered(): void {
    const p = this.controller.pointer;
    const store = useGameStore.getState();
    if (!p.inside) {
      if (store.hovered !== null) store.setHovered(null);
      return;
    }
    const world = this.camera.screenToWorld(p.x, p.y);
    const g = worldToGrid(world.x, world.y);
    const tx = Math.floor(g.x + 0.5);
    const ty = Math.floor(g.y + 0.5);
    if (!this.map.inBounds(tx, ty)) {
      store.setHovered(null);
      return;
    }
    const hovered: HoveredTile = {
      x: tx,
      y: ty,
      terrain: this.map.terrainAt(tx, ty),
      biome: this.map.biomeAt(tx, ty),
      buildable: this.map.isBuildable(tx, ty),
      elevation: this.map.elevationAt(tx, ty),
    };
    store.setHovered(hovered);
  }

  private pushTelemetry(dtMs: number): void {
    this.tpsWindowMs += dtMs;
    if (this.tpsWindowMs >= 1000) {
      this.tpsDisplay = Math.round((this.tpsCounter * 1000) / this.tpsWindowMs);
      this.tpsCounter = 0;
      this.tpsWindowMs = 0;
    }

    this.telemetryAcc += dtMs;
    if (this.telemetryAcc < TELEMETRY_INTERVAL_MS || !this.app) return;
    this.telemetryAcc = 0;

    let trips = 0;
    let workers = 0;
    for (const e of this.sim.world.query(CWorker)) {
      const w = this.sim.world.get(e, CWorker);
      if (w) {
        trips += w.trips;
        workers++;
      }
    }

    const stats = this.renderer.stats;
    useGameStore.getState().setTelemetry({
      fps: Math.round(this.app.ticker.FPS),
      tps: this.tpsDisplay,
      tick: this.sim.tick,
      entityCount: this.sim.world.entityCount(),
      zoom: Math.round(this.camera.zoom * 100) / 100,
      chunks: stats.chunks,
      visibleChunks: stats.visibleChunks,
      workers,
      roads: this.sim.roads.count(),
      trips,
    });
  }

  get expectedTps(): number {
    return TICKS_PER_SECOND;
  }

  get gameMap(): GameMap {
    return this.map;
  }

  viewportGridQuad(): Vec2[] {
    const v = this.camera.visibleWorldRect();
    const corners: Vec2[] = [
      { x: v.x, y: v.y },
      { x: v.x + v.w, y: v.y },
      { x: v.x + v.w, y: v.y + v.h },
      { x: v.x, y: v.y + v.h },
    ];
    return corners.map((c) => worldToGrid(c.x, c.y));
  }

  moveToGrid(gridX: number, gridY: number): void {
    const w = gridToWorld(gridX, gridY);
    this.camera.centerOn(w.x, w.y);
  }

  destroy(): void {
    const canvas = this.app?.canvas;
    if (canvas) {
      canvas.removeEventListener('pointerdown', this.onToolDown);
      canvas.removeEventListener('pointermove', this.onToolMove);
    }
    window.removeEventListener('pointerup', this.onToolUp);
    this.controller?.destroy();
    this.renderer?.destroy();
    this.fog?.destroy();
    this.unitRenderer?.destroy();
    this.app?.destroy(true, { children: true, texture: true });
    this.app = null;
    useGameStore.getState().setReady(false);
  }
}
